import { useDesktopViewer } from '../hooks/useDesktopViewer';
import { cn } from '@/shared/utils/utils';
import { X } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { HEAT_MAP_STOPS, type HeatMapIssueRange } from '../utils/heatMap';

interface HeatMapLegendProps {
  filter: HeatMapIssueRange;
  onFilterChange: (filter: HeatMapIssueRange) => void;
  onClose: () => void;
}

/** In normal flow below the mobile canvas; overlays the canvas on desktop. */
export function HeatMapLegend({ filter, onFilterChange, onClose }: HeatMapLegendProps) {
  const desktop = useDesktopViewer();
  return (
    <section
      aria-label="Heat map legend"
      data-tour-id="iv-heatmap-panel"
      className={cn(
        'z-30 min-w-0 shrink-0 border-t border-border bg-heatmap-panel text-card-foreground',
        desktop && 'absolute bottom-4 right-4 w-64 rounded-2xl border shadow-xl'
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-2 border-b border-border/70 px-3',
          desktop && 'px-4 py-2'
        )}
      >
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Heat map
          </h3>
          <p className={cn('text-xs text-foreground', desktop && 'mt-0.5 text-sm')}>
            Open issue density
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0"
          onClick={onClose}
          aria-label="Hide heat map"
        >
          <X />
        </Button>
      </div>
      <div className={cn('flex flex-col gap-1 px-3 py-2', desktop && 'flex-row gap-4 p-4')}>
        <div
          aria-hidden="true"
          className={cn(
            'h-2 w-full shrink-0 rounded-full bg-[linear-gradient(90deg,var(--heatmap-none),var(--heatmap-low),var(--heatmap-medium),var(--heatmap-high))]',
            desktop &&
              'h-44 w-5 bg-[linear-gradient(0deg,var(--heatmap-none),var(--heatmap-low),var(--heatmap-medium),var(--heatmap-high))]'
          )}
        />
        <div
          className={cn(
            'grid min-w-0 flex-1 grid-cols-4 gap-1',
            desktop && 'flex flex-col-reverse justify-between'
          )}
        >
          {HEAT_MAP_STOPS.map((stop) => (
            <Button
              key={stop.range}
              variant="ghost"
              className={cn(
                'h-11 min-w-0 gap-1 px-1 text-xs',
                desktop && 'justify-start gap-2 px-2',
                // Touch browsers can retain hover after the opener disappears.
                // Only an active filter should have a filled background.
                filter === stop.range
                  ? 'bg-accent hover:bg-accent ring-1 ring-inset ring-border'
                  : 'hover:bg-transparent hover:text-muted-foreground'
              )}
              aria-label={stop.label}
              aria-pressed={filter === stop.range}
              onClick={() => onFilterChange(filter === stop.range ? 'all' : stop.range)}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: stop.color }}
              />
              <span>{desktop ? stop.label : stop.range}</span>
            </Button>
          ))}
        </div>
      </div>
      {filter !== 'all' && (
        <div className="border-t border-border/70 px-3">
          <Button
            variant="ghost"
            className="h-11 px-1 text-xs"
            onClick={() => onFilterChange('all')}
          >
            Clear filter
          </Button>
        </div>
      )}
    </section>
  );
}
